import { UserRoleEnum } from './user.interface';
import { FirebaseTimestamp } from './_common.interface';

export interface ProjectModel {
  id?: string;
  name?: string;
  classroomId?: string;
  createdAt?: Date;
  updatedAt?: Date;
  description?: string;
  status?: ProjectStatus;
  // Survey-related fields
  startDate?: FirebaseTimestamp | Date; // Use FirebaseTimestamp for consistency
  startDateTime?: string;
  startDateMode?: 'month' | 'date';
  // Project management fields
  studentLimit?: number;
  isActive?: boolean;
  isDefaultProject?: boolean; // Mark which project is the default for the classroom

  // Timeline-specific fields
  timelineSteps?: ProjectTimelineStep[];
  publishingKitClaimed?: boolean;
  publishingKitClaimedAt?: FirebaseTimestamp;
  preSessionFlyersSent?: boolean;
  preSessionFlyersSentAt?: FirebaseTimestamp;
  postSessionFlyersSent?: boolean;
  postSessionFlyersSentAt?: FirebaseTimestamp;
  parentOrderDeadline?: FirebaseTimestamp;
  booksShippedDeadline?: FirebaseTimestamp;
  celebrationCompletedDeadline?: FirebaseTimestamp;

  // Calculated timeline fields
  currentTimelineStep?: TimelineStepId;
  timelineProgress?: number; // 0-100%
  timelineAnalytics?: ProjectTimelineAnalytics;

  // Project template link (WordPress project)
  projectTemplateId?: string;
}

// Timeline analytics interface
export interface ProjectTimelineAnalytics {
  currentStep: TimelineStepId;
  progress: number; // 0-100
  completedSteps: TimelineStepId[];
  nextAction?: TimelineAction;
  lastUpdated: FirebaseTimestamp;
  estimatedCompletionDate?: FirebaseTimestamp;
}

export interface ProjectTimelineStep {
  id: TimelineStepId;
  title: string;
  description: string;
  isCompleted: boolean;
  completedAt?: FirebaseTimestamp;
  dueDate?: FirebaseTimestamp;
  requiredRole: UserRoleEnum[];
  dependencies: TimelineStepId[];
  actions?: TimelineAction[];
  order: number;
}

export enum TimelineActionType {
  NAVIGATE = 'navigate',
  MODAL = 'modal',
  API_CALL = 'api_call',
}

// Timeline action interface
export interface TimelineAction {
  id: string;
  label: string;
  action: TimelineActionType;
  target?: string;
  requiredRole: UserRoleEnum[];
  isEnabled?: boolean;
}

// Timeline step enums
export enum TimelineStepId {
  PUBLISHING_KIT_CLAIMED = 'publishing-kit-claimed',
  SEND_PRE_SESSION_FLYERS = 'send-pre-session-flyers',
  PRINT_STUDENT_LOGINS = 'print-student-logins',
  START_DATE_WITH_CLASS = 'start-date-with-class',
  STUDENTS_LOGIN = 'students-login',
  BOOKS_COMPLETE = 'books-complete',
  PARENT_ORDER_DEADLINE = 'parent-order-deadline',
  SHIP_BOOKS = 'ship-books',
  CELEBRATE = 'celebrate',
}

export enum ProjectStatus {
  DRAFT = 'draft',
  IN_PROGRESS = 'in_progress',
  COMPLETED = 'completed',
}

export interface ProjectWithStudents extends ProjectModel {
  students?: StudentModel[];
  studentCount?: number;
}

export interface StudentModel {
  id: string;
  email: string;
  firstName?: string;
  lastName?: string;
  studentName?: string;
  loginName?: string;
  classroomId?: string;
  projectId?: string;
  teacherEmail?: string;
  generatedEmail?: boolean;
  encryptedPassword?: string;
  submissions?: {
    projectId: string;
    submittedAt: Date;
    status: string;
    content?: any;
  }[];
}

export interface DashboardProjectModel {
  bookCreatedStudents: number;
  parentResponses: number;
  studentsWithOrders: number;
}
